import React, { useEffect, useState } from "react";
import apis from "../utils/apis";
import httpAction from "../utils/httpAction";
import useSiteSettings from "../hooks/useSiteSettings";
import { CardSkeleton, SkeletonText } from "../components/common/Skeleton";

const inputStyle = {
  width: "100%",
  padding: 12,
  border: "1px solid #d0d5dd",
  borderRadius: 12,
};

export default function Contact() {
  const { settings, loading } = useSiteSettings();
  const [branches, setBranches] = useState([]);
  const [branchLoading, setBranchLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  useEffect(() => {
    httpAction({ url: apis().branchList, silent: true }).then((result) => {
      if (result?.status) setBranches(result.list || []);
      setBranchLoading(false);
    });
  }, []);

  const submitHandler = async (e) => {
    e.preventDefault();
    setSending(true);
    const result = await httpAction({
      url: apis().contactSubmit,
      method: "POST",
      body: form,
    });
    setSending(false);
    if (result?.status)
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
  };

  if (loading)
    return (
      <div className="container" style={{ padding: "120px 0 60px" }}>
        <SkeletonText width="260px" height={36} />
        <div style={{ marginTop: 24 }}>
          <CardSkeleton count={2} height={260} />
        </div>
      </div>
    );

  return (
    <div style={{ background: "#f7f8fc" }}>
      <section
        style={{
          padding: "90px 0 40px",
          background: "#0d1b2a",
          color: "#fff",
        }}
      >
        <div className="container">
          <h1
            style={{
              fontSize: "clamp(2rem,4vw,3.5rem)",
              marginBottom: 12,
            }}
          >
            {settings.contactHeroTitle || "Contact Us"}
          </h1>
          <p style={{ maxWidth: 720, color: "rgba(255,255,255,.8)" }}>
            {settings.contactHeroDescription}
          </p>
        </div>
      </section>

      <section className="container" style={{ padding: "40px 0 70px" }}>
        <div className="row">
          <form
            className="col-md-6"
            onSubmit={submitHandler}
            style={{
              background: "#fff",
              borderRadius: 20,
              padding: 24,
              boxShadow: "0 12px 30px rgba(15,23,42,.06)",
            }}
          >
            <h3>{settings.contactFormTitle}</h3>
            <p style={{ color: "#667085", marginBottom: 20 }}>
              {settings.contactFormDescription}
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 14,
              }}
            >
              <input
                placeholder="Your name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                style={inputStyle}
              />
              <input
                placeholder="Phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                style={inputStyle}
              />
              <input
                placeholder="Email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                style={inputStyle}
              />
              <input
                placeholder="Subject"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                style={inputStyle}
              />
            </div>
            <textarea
              rows="6"
              placeholder="Message"
              required
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              style={{ ...inputStyle, marginTop: 14 }}
            />
            <button
              type="submit"
              disabled={sending}
              style={{
                marginTop: 16,
                padding: "14px 22px",
                background: "#0f5ef7",
                color: "#fff",
                border: "none",
                borderRadius: 12,
                fontWeight: 700,
              }}
            >
              {sending ? "Sending..." : "Send Message"}
            </button>
          </form>

          <div className="col-md-6" style={{ display: "grid", gap: 18 }}>
            <div
              style={{
                background: "#fff",
                borderRadius: 20,
                padding: 24,
                boxShadow: "0 12px 30px rgba(15,23,42,.06)",
              }}
            >
              <h3>Direct Contact</h3>
              <p>
                <strong>Phone:</strong>{" "}
                <a href={`tel:${settings.contactPhone}`}>
                  {settings.contactPhone}
                </a>
              </p>
              <p>
                <strong>Email:</strong>{" "}
                <a href={`mailto:${settings.contactEmail}`}>
                  {settings.contactEmail}
                </a>
              </p>
              <p>
                <strong>Address:</strong> {settings.officeAddress}
              </p>
              <p>
                <strong>Hours:</strong> {settings.officeHours}
              </p>
              {!!settings.googleMapLink && (
                <div
                  style={{
                    position: "relative",
                    height: "160px",
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                >
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3407.2087842889428!2d74.23722099999999!3d31.3532173!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x391901e838ff5401%3A0xf80e2592944e121f!2sHafiz%20Brothers%20builders%20and%20developers!5e0!3m2!1sen!2s!4v1777113308912!5m2!1sen!2s"
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    style={{ borderRadius: "8px" }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Google Map"
                  ></iframe>
                </div>
              )}
            </div>
            <div
              style={{
                background: "#fff",
                borderRadius: 20,
                padding: 24,
                boxShadow: "0 12px 30px rgba(15,23,42,.06)",
              }}
            >
              <h3>Our Branches</h3>
              <div style={{ display: "grid", gap: 12 }}>
                {branchLoading ? (
                  <CardSkeleton count={3} height={120} />
                ) : (
                  branches.map((branch) => (
                    <div
                      key={branch._id}
                      style={{
                        border: "1px solid #eef2f6",
                        borderRadius: 14,
                        padding: 14,
                      }}
                    >
                      <div style={{ fontWeight: 700 }}>{branch.name}</div>
                      <div style={{ color: "#667085", margin: "6px 0" }}>
                        {branch.address}
                      </div>
                      <a href={`tel:${branch.mobile}`}>{branch.mobile}</a>
                      {branch.mapLink ? (
                        <div>
                          <a
                            href={branch.mapLink}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Get directions
                          </a>
                        </div>
                      ) : null}
                    </div>
                  ))
                )}
                {!branchLoading && !branches.length && (
                  <div style={{ color: "#667085" }}>No branches added yet.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
